<template>
    <ClientOnly>
        <VJoyIconButton
            v-if="sortablePortfolioItems.length > 0"
            data-testid="portfolio-item-porfolio-sort-link"
            variant="secondary"
            icon="sort"
            size="small"
            :label="t('profile.section.portfolio.sort.aria.label')"
            @click="openSortDialog"
        />
        <template #fallback>
            <SectionActionSkeleton />
        </template>
    </ClientOnly>
    <VJoyDialog
        id="portfolioSortDialog"
        ref="sortDialog"
        data-testid="portfolio-sort-dialog"
        :cancel-text="t('general.cancel')"
        @dialog:hide="handleSortDialogHide"
        @dialog:cancel="handleSortDialogHide"
    >
        <template #dialog-header>
            <VJoyText tag-name="h3" preset="primary-850">{{ t('profile.section.portfolio.sort.dialog.title') }}</VJoyText>
        </template>
        <template #dialog-subheader>
            <VJoyText preset="primary-400">{{ t('profile.section.portfolio.sort.dialog.subtitle') }}</VJoyText>
        </template>
        <template #dialog-body>
            <VJoyWrapper class="portfolio-sort-dialog joy-mt-2" direction="column" align="stretch" no-margin>
                <Draggable
                    v-model="sortablePortfolioItems"
                    class="portfolio-sort-dialog__list"
                    :group="{name: 'portfolio-sort', put: false, pull: false}"
                    draggable=".portfolio-sort-dialog__draggable-item"
                    handle=".portfolio-sort-dialog__draggable-icon"
                    tag="ul"
                >
                    <li
                        v-for="(item, index) in sortablePortfolioItems"
                        :key="item.id"
                        :data-testid="`portfolio-sort-item-${index}`"
                        class="portfolio-sort-dialog__item portfolio-sort-dialog__draggable-item"
                    >
                        <VJoyWrapper justify="space-between" align="center" wrap="nowrap" column-gap="3" no-margin>
                            <VJoyWrapper align="center" column-gap="3" wrap="nowrap" no-margin>
                                <MaltImg
                                    :src="item.thumbnail"
                                    :title="item.title"
                                    :alt="item.title"
                                    densities="1x"
                                    class="portfolio-sort-dialog__thumb"
                                />
                                <VJoyWrapper direction="column" row-gap="1" align="flex-start" no-margin class="portfolio-sort-dialog__item-content">
                                    <VJoyText preset="primary-450" tag-name="p" class="portfolio-sort-dialog__item-title">
                                        {{ item.title || t('profile.section.portfolio.item.untitled') }}
                                    </VJoyText>
                                    <VJoyText preset="primary-300" tag-name="p" class="portfolio-sort-dialog__item-subtitle">
                                        {{ item.description || '' }}
                                    </VJoyText>
                                </VJoyWrapper>
                            </VJoyWrapper>
                            <VJoyWrapper align="center" column-gap="6" wrap="nowrap" no-margin>
                                <VJoyIconButton
                                    variant="ghost"
                                    size="large"
                                    icon="trash"
                                    class="portfolio-sort-dialog__remove-button"
                                    :label="t('general.delete')"
                                    @mousedown.stop
                                    @touchstart.stop
                                    @click="openDeleteItemConfirmDialog(item.id)"
                                />
                                <VJoyIcon size="small" name="menu" class="portfolio-sort-dialog__draggable-icon" />
                            </VJoyWrapper>
                        </VJoyWrapper>
                    </li>
                </Draggable>
            </VJoyWrapper>
        </template>
        <template #dialog-confirm>
            <VJoyButton variant="main" size="small" @click="reorderPortfolioItems">{{ t('general.save') }}</VJoyButton>
        </template>
    </VJoyDialog>
    <VJoyDialog
        ref="deleteItemConfirmDialog"
        pattern-confirm
        :cancel-text="t('profile.section.portfolio.sort.delete.dialog.confirm')"
        :confirm-text="t('general.cancel')"
        @dialog:confirm="deleteItemConfirmDialog?.hide({fireHideEvent: false})"
        @dialog:cancel="deleteItemConfirmDialog?.hide({fireHideEvent: false})"
    >
        <template #dialog-header>
            <VJoyText tag-name="h3" preset="primary-450">{{ t('profile.section.portfolio.sort.delete.dialog.title') }}</VJoyText>
        </template>
        <template #dialog-body>
            <VJoyText preset="primary-400">{{ t('profile.section.portfolio.sort.delete.dialog.description') }}</VJoyText>
        </template>
        <template #dialog-cancel>
            <VJoyButton
                icon="trash"
                :label="t('profile.section.portfolio.sort.delete.dialog.confirm')"
                size="small"
                variant="ghost"
                @click="deleteItemAndCloseDialog"
            />
        </template>
    </VJoyDialog>
    <VJoyDialog
        ref="closeWithoutSavingConfirmDialog"
        :pattern-confirm="true"
        :cancel-text="t('profile.section.portfolio.sort.close.dialog.keep.organizing')"
        :confirm-text="t('profile.section.portfolio.sort.close.dialog.confirm')"
        @dialog:confirm="confirmCloseWithoutSaving"
        @dialog:hide="keepOrganizing"
    >
        <template #dialog-header>
            <VJoyText tag-name="h3" preset="primary-450">{{ t('profile.section.portfolio.sort.close.dialog.title') }}</VJoyText>
        </template>
        <template #dialog-body>
            <VJoyText preset="primary-400">{{ t('profile.section.portfolio.sort.close.dialog.description') }}</VJoyText>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import {VueDraggable as Draggable} from 'vue-draggable-plus';
    import {MaltImg} from '#components';
    import {useTranslation} from '#imports';
    import {VJoyButton, VJoyIconButton, VJoyDialog, VJoyIcon, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import type {PortfolioItemSummaryResource} from '@api';
    import SectionActionSkeleton from '~/components/section/SectionActionSkeleton.vue';

    const {t} = useTranslation();

    const props = defineProps<{
        portfolioItems: PortfolioItemSummaryResource[];
    }>();

    const emit = defineEmits<{
        reorder: [portfolioItems: PortfolioItemSummaryResource[]];
        deletePortfolioItem: [portfolioItemId: string];
    }>();

    const sortDialog = ref<InstanceType<typeof VJoyDialog> | null>(null);
    const sortablePortfolioItems = ref<PortfolioItemSummaryResource[]>(props.portfolioItems);

    function openSortDialog() {
        sortablePortfolioItems.value = props.portfolioItems;
        sortDialog.value?.show();
    }

    async function reorderPortfolioItems() {
        emit('reorder', sortablePortfolioItems.value);
        await sortDialog.value?.hide({fireHideEvent: false});
    }

    // ##### Sort dialog hide
    async function handleSortDialogHide() {
        if (!hasUnsavedChanges.value) {
            return;
        }
        await closeWithoutSavingConfirmDialog.value?.show();
    }

    const hasUnsavedChanges = computed(
        () => getPortfolioItemsSignature(sortablePortfolioItems.value) !== getPortfolioItemsSignature(props.portfolioItems),
    );

    function getPortfolioItemsSignature(items: PortfolioItemSummaryResource[]): string {
        return items.map((item) => item.id ?? '').join('|');
    }

    // ##### Close without saving dialog
    const closeWithoutSavingConfirmDialog = useTemplateRef<InstanceType<typeof VJoyDialog> | null>('closeWithoutSavingConfirmDialog');
    async function confirmCloseWithoutSaving() {
        sortablePortfolioItems.value = [...props.portfolioItems];
        await closeWithoutSavingConfirmDialog.value?.hide({fireHideEvent: false});
    }

    async function keepOrganizing() {
        await closeWithoutSavingConfirmDialog.value?.hide({fireHideEvent: false});
        await sortDialog.value?.show();
    }

    // ##### Delete item dialog
    const deleteItemConfirmDialog = useTemplateRef<InstanceType<typeof VJoyDialog> | null>('deleteItemConfirmDialog');
    const itemToDeleteId = ref<string | null>(null);

    function openDeleteItemConfirmDialog(itemId: string) {
        itemToDeleteId.value = itemId;
        deleteItemConfirmDialog.value?.show();
    }

    async function deleteItemAndCloseDialog() {
        if (!itemToDeleteId.value) {
            return;
        }

        emit('deletePortfolioItem', itemToDeleteId.value);
        await removeSortPreviewItem(itemToDeleteId.value);
        itemToDeleteId.value = null;
        await deleteItemConfirmDialog.value?.hide({fireHideEvent: false});
    }

    async function removeSortPreviewItem(itemId: string) {
        sortablePortfolioItems.value = sortablePortfolioItems.value.filter((item) => item.id !== itemId);
        if (sortablePortfolioItems.value.length === 0) {
            await sortDialog.value?.hide({fireHideEvent: false});
        }
    }
</script>

<style scoped lang="scss">
    .portfolio-sort-dialog {
        &__list {
            width: 100%;
            list-style: none;
            margin: 0;
            padding: 0;
        }

        &__item {
            width: 95%;
            margin-bottom: var(--joy-core-spacing-6);
        }

        &__item:nth-child(4) {
            border-top: 1px solid var(--joy-color-neutral-30);
            padding-top: var(--joy-core-spacing-6);
        }

        &__thumb {
            border-radius: var(--joy-core-radius-2);
            object-fit: cover;
            width: 48px;
            height: 48px;
        }

        &__item-content {
            min-width: 0;
            flex: 1;
            max-width: min(332px, 40vw);
        }

        &__item-title {
            margin: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__item-subtitle {
            margin: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__draggable-icon {
            cursor: grab;

            &:active {
                cursor: grabbing;
            }

            @media screen and (max-width: 600px) {
                position: relative;

                &::after {
                    content: '';
                    position: absolute;
                    inset: calc(-1 * var(--joy-core-spacing-3));
                }
            }
        }

        &__remove-button :deep(.joy-icon),
        &__remove-button:hover :deep(.joy-icon) {
            color: var(--joy-color-error-50) !important; // keep visual parity with Figma red trash icon
        }
    }
</style>
